import { Link, useSearchParams } from 'react-router-dom';
import { Linkedin, Github, Mail, type LucideIcon } from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { TextSplit, MaskReveal } from '@/components/motion/Reveal';
import { Skeleton } from '@/components/ui/Skeleton';
import { useTeamMembers } from '@/lib/cms';
import { DEPARTMENT_VALUES, type Department, type TeamMember } from '@/lib/cms-types';

const DEPARTMENT_LABELS: Record<Department | 'all', string> = {
  all: 'All',
  leadership: 'Leadership',
  engineering: 'Engineering',
  design: 'Design',
  product: 'Product',
  operations: 'Operations',
};

const DEPARTMENTS: Array<{ value: 'all' | Department; label: string }> = [
  { value: 'all', label: DEPARTMENT_LABELS.all },
  ...DEPARTMENT_VALUES.map((d) => ({ value: d, label: DEPARTMENT_LABELS[d] })),
];

export function TeamPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dept = searchParams.get('dept') ?? 'all';
  const { data: members = [], isLoading } = useTeamMembers(dept);

  const list = members;

  function setDept(value: string) {
    if (value === 'all') {
      searchParams.delete('dept');
    } else {
      searchParams.set('dept', value);
    }
    setSearchParams(searchParams);
  }

  return (
    <>
      <Meta
        title="Team"
        description="The people behind Vonlof — a small, senior team of engineers, designers, and operators."
        path="/team"
      />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <MaskReveal className="container-wide" duration={1}>
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Team</p>
          </FadeIn>
          <TextSplit
            text="The people behind the work."
            className="mt-3 display-serif text-display-l text-fg"
            delay={0.1}
            stagger={0.06}
          />
        </MaskReveal>

        <section className="container-wide">
          <FadeIn delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Filter by department">
              {DEPARTMENTS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  role="tab"
                  aria-selected={dept === d.value}
                  onClick={() => setDept(d.value)}
                  className={
                    dept === d.value
                      ? 'rounded-full border border-gold-500/40 bg-gold-500/10 px-3 py-1.5 text-xs font-medium text-gold-500'
                      : 'rounded-full border border-border bg-bg-elevated px-3 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:border-gold-500/40 hover:text-fg'
                  }
                >
                  {d.label}
                </button>
              ))}
            </div>
          </FadeIn>

          {isLoading ? (
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="h-full overflow-hidden">
                  <Skeleton className="aspect-square w-full" />
                  <CardHeader>
                    <Skeleton className="h-5 w-1/2" />
                    <Skeleton className="mt-2 h-4 w-3/4" />
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : list.length === 0 ? (
            <Card className="mt-12 p-10 text-center text-sm text-fg-subtle">
              No team members in this department yet.
            </Card>
          ) : (
            <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((m) => (
                <StaggerItem key={m.id}>
                  <TeamCard member={m} />
                </StaggerItem>
              ))}
            </Stagger>
          )}

          <FadeIn delay={0.2}>
            <div className="mt-20 rounded-xl border border-border bg-bg-elevated/40 p-8 text-center">
              <p className="display-serif text-2xl text-fg">We're hiring.</p>
              <p className="mt-2 text-sm text-fg-muted">
                Senior engineers, designers, and product folks who care about craft.
              </p>
              <div className="mt-4">
                <Button asChild>
                  <Link to="/careers">See open roles</Link>
                </Button>
              </div>
            </div>
          </FadeIn>
        </section>
      </main>
    </>
  );
}

function TeamCard({ member }: { member: TeamMember }) {
  const initials = member.name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <Card className="group h-full overflow-hidden transition-all hover:-translate-y-1 hover:border-gold-500/40 hover:shadow-gold">
      <div className="relative aspect-square w-full overflow-hidden bg-gradient-to-br from-gold-500/15 to-bg-elevated">
        {member.portrait_url ? (
          <img
            src={member.portrait_url}
            alt={`${member.name} portrait`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Avatar className="h-32 w-32 text-3xl">
              <AvatarFallback className="bg-gold-500/15 text-gold-500">{initials || '?'}</AvatarFallback>
            </Avatar>
          </div>
        )}
      </div>
      <CardHeader>
        <CardTitle>{member.name}</CardTitle>
        <CardDescription>{member.role}</CardDescription>
        {member.bio && <p className="mt-2 text-sm text-fg-muted line-clamp-3">{member.bio}</p>}
        {member.skills && member.skills.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {member.skills.slice(0, 4).map((s) => (
              <Badge key={s} variant="outline" className="text-[10px]">
                {s}
              </Badge>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-center gap-2">
          {member.linkedin_url && (
            <SocialLink href={member.linkedin_url} label="LinkedIn" icon={Linkedin} />
          )}
          {member.github_url && (
            <SocialLink href={member.github_url} label="GitHub" icon={Github} />
          )}
          {member.email && (
            <SocialLink href={`mailto:${member.email}`} label="Email" icon={Mail} />
          )}
        </div>
      </CardHeader>
    </Card>
  );
}

function SocialLink({ href, label, icon: Icon }: { href: string; label: string; icon: LucideIcon }) {
  return (
    <a
      href={href}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-fg-muted transition-colors hover:border-gold-500/40 hover:text-gold-500"
    >
      <Icon className="h-3.5 w-3.5" />
    </a>
  );
}